import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import FormatValue from 'assets/components/formatValue/FormatValue';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import TextBox, { textBoxStyles } from 'assets/components/textBox/TextBox';
import { getLocales } from 'assets/locales/Locale';
import ValueDisplay from 'assets/templates/valueDisplay/ValueDisplay';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { isEmpty } from 'lodash';
import PurchaseState from 'models/enums/PurchaseState';
import ContractRefundPriceSummariesTable from '../../../components/ContractRefundPriceSummariesTable';
import { ContractRefundForm, useContractRefundProvider } from '../../../Sales.ContractRefund.Hooks';
import { refundInfoGridStyles } from './Sales.ContractRefund.Process.Info';

const SalesContractRefundProcessContract = () => {
  const { lang, translate } = getLocales();
  const { form } = useModalForm<ContractRefundForm>();
  const { contract, vehicle } = useContractRefundProvider();
  return (
    <FormWrapper>
      <TextBox
        class={textBoxStyles.block.flexBlock}
        title={lang.sumOfClaimAmountsPaidOnContract}
        style={{ fontSize: Theme.FontSize.XL }}
        subtitle={<FormatValue value={`$${form.data?.sumOfClaimAmountsPaidOnContract}`} />}
      />
      <br />
      <hr />
      <br />
      {(!isEmpty(form.data?.enhancements) || !isEmpty(form.data?.subfees)) && (
        <>
          <div style={{ ...refundInfoGridStyles, gridTemplateColumns: 'repeat(2, 1fr)' }}>
            {!isEmpty(form.data?.enhancements) && (
              <TextBox
                class={textBoxStyles.block.flexBlock}
                title={lang.enhancements}
                subtitle={
                  <ul>
                    {form.data?.enhancements?.map((it) => {
                      return <li key={it.enhancementId}>{it.name}</li>;
                    })}
                  </ul>
                }
              />
            )}
            {!isEmpty(form.data?.subfees) && (
              <TextBox
                class={textBoxStyles.block.flexBlock}
                title={lang.surcharges}
                subtitle={
                  <ul>
                    {form.data?.subfees?.map((it) => {
                      return <li key={it.subfeeId}>{it.name}</li>;
                    })}
                  </ul>
                }
              />
            )}
          </div>
          <br />
          <hr />
          <br />
        </>
      )}
      <h3>{lang.vehicle}</h3>
      <div style={refundInfoGridStyles}>
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.purchaseState}
          subtitle={translate(
            vehicle?.purchaseState === PurchaseState.USED ? PurchaseState.PRE_OWNED : vehicle?.purchaseState
          )}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.purchaseType}
          subtitle={translate(vehicle?.purchaseType) ?? 'N/A'}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.startingOdometer}
          subtitle={<ValueDisplay value={`${vehicle?.posOdometerReading} km`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.maxCoverageKm}
          subtitle={<ValueDisplay value={`${contract?.maxCoverageKm ?? 0} km`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.kmsUsed}
          subtitle={<ValueDisplay value={`${form.data.usedOdometerReading} km`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.kmConsumed}
          subtitle={<ValueDisplay value={`${form.data.usedOdometerReadingPercentage ?? 0} %`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.kmRemaining}
          subtitle={<ValueDisplay value={`${form.data.remainingOdometerReadingPercentage ?? 0} %`} />}
        />
      </div>
      <br />
      <hr />
      <br />
      <h3>{lang.termAndExpiryInformation}</h3>
      <div style={refundInfoGridStyles}>
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.effectiveDate}
          subtitle={localizeDate(contract?.effectiveDate, defaultDateTimeFormat)}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.expiryDate}
          subtitle={localizeDate(contract?.expiryDate, defaultDateTimeFormat)}
        />
        <TextBox class={textBoxStyles.block.flexBlock} title={lang.termInDays} subtitle={form.data?.termInDays} />
        <TextBox class={textBoxStyles.block.flexBlock} title={lang.daysConsumed} subtitle={form.data?.usedTermInDays} />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.daysRemaining}
          subtitle={form.data?.remainingTermInDays}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.termConsumed}
          subtitle={form.data?.usedTermInDaysPercentage}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.termRemaining}
          subtitle={form.data?.remainingTermInDaysPercentage}
        />
      </div>
      <br />
      <hr />
      <br />
      <h3 style={{ paddingBottom: '0' }}>{lang.contractPricing}</h3>
      <div style={{ ...refundInfoGridStyles, gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>
            <strong>{lang.dealerCost}</strong>
          </span>
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.paymentPrice}
            subtitle={
              <FormatValue
                value={`$${contract?.originalPriceCAD + contract?.discountPriceCAD + contract?.distributorPriceCAD}`}
              />
            }
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.enhacementsPriceCAD}
            subtitle={<FormatValue value={`$${contract?.enhancementsPriceCAD}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.surchargesPriceCAD}
            subtitle={<FormatValue value={`$${contract?.surchargesPriceCAD}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.paymentTaxes}
            subtitle={<FormatValue value={`$${contract?.dealerCostTaxAmt}`} />}
          />
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.totalPriceAmountWPaymentTaxes}
            subtitle={<FormatValue value={`$${contract?.dealerCostTotal}`} />}
          />
        </div>
        <div>
          <span>
            <strong>{lang.salePrice}</strong>
          </span>
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.salePrice}
            subtitle={<FormatValue value={`$${contract?.salePriceCAD}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.salePriceTaxes}
            subtitle={<FormatValue value={`$${contract?.totalTaxCAD}`} />}
          />
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.totalRetailPriceAmountWSalePriceTaxes}
            subtitle={<FormatValue value={`$${contract?.salePriceWithTaxCAD}`} />}
          />
        </div>
        <div>
          <span>
            <strong>{lang.dealerProfitNonASC}</strong>
          </span>
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.dealerProfit}
            subtitle={<FormatValue value={`$${contract?.dealerProfit}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.dealerProfitTax}
            subtitle={<FormatValue value={`$${contract?.dealerProfitTaxAmt}`} />}
          />
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.totalDealerProfitWDealerProfitTaxes}
            subtitle={<FormatValue value={`$${contract?.dealerProfitWithTaxCAD}`} />}
          />
        </div>
      </div>
      &nbsp;{' '}
      <ContractRefundPriceSummariesTable contract={contract} contractRefund={form.data as Model.IContractRefund} />
    </FormWrapper>
  );
};

export default SalesContractRefundProcessContract;
